/**
 * Driving Directions Deep-Link Utility
 *
 * Builds a platform-aware URL that opens the user's native maps app
 * with directions to the given address pre-filled. iOS users land in
 * Apple Maps; everyone else lands in Google Maps (which works on
 * Android, Windows, macOS, Linux, and as a web fallback).
 *
 * Why deep-link instead of in-app routing?
 *   - Native apps already have the user's saved home address, vehicle
 *     mode, traffic data, and offline maps.
 *   - Avoids the API spend + complexity of an in-app routing layer.
 *   - Users universally expect "Directions" to open their preferred
 *     map app on mobile.
 *
 * The functions here return URL strings; calling code typically does:
 *   <a href={getDirectionsUrl(event)} target="_blank" rel="noreferrer">Directions</a>
 *
 * Optionally `openDirections(event)` performs the navigation directly
 * (window.open with proper target/rel) for click handlers.
 */

import type { ScheduledEvent } from '../services/scheduledEventsService';

/**
 * Stitch together the address parts on a ScheduledEvent into a single
 * destination string suitable for use as a maps query.
 *
 * Returns null when the event has no usable address — caller should
 * gate the "Directions" button on a non-null result.
 */
export function buildDestinationQuery(event: ScheduledEvent): string | null {
    return buildDestinationQueryFromParts(event);
}

/**
 * The address-parts form of the above, for records that aren't
 * ScheduledEvents (a WorkOrder carries the same five fields under
 * project_* names). Every part is optional; null means there was
 * nothing to navigate to.
 */
export interface AddressParts {
    address?: string | null;
    addressLine2?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
}

export function buildDestinationQueryFromParts(parts: AddressParts): string | null {
    const present = [
        parts.address,
        parts.addressLine2,
        parts.city,
        parts.state,
        parts.zip,
    ].filter(Boolean) as string[];

    if (present.length === 0) return null;
    return present.join(', ');
}

/**
 * Detect whether the current browser is running on an iOS device.
 * Used to choose between Apple Maps and Google Maps deep-links.
 *
 * Two checks because newer iPads identify as Mac when "Request Desktop
 * Site" is on; the touch-points heuristic catches that case.
 */
function isIosDevice(): boolean {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) return true;
    // iPadOS 13+ pretends to be Mac in UA but reports touch capability.
    if (/Mac/.test(ua) && typeof navigator.maxTouchPoints === 'number' && navigator.maxTouchPoints > 1) {
        return true;
    }
    return false;
}

/**
 * Build the directions URL for the given event.
 * Returns null when the event has no usable address.
 *
 * The Apple Maps URL scheme accepts `daddr` as the destination param.
 * Google Maps' universal "directions" URL takes `destination` as a
 * URL-encoded address string and routes from the user's current
 * location.
 *
 * Both URL schemes work as both http(s) links (which open the
 * native app on supported platforms via OS-level association) and
 * as web fallbacks when no native app is registered.
 */
export function getDirectionsUrl(event: ScheduledEvent): string | null {
    return getDirectionsUrlFromParts(event);
}

/**
 * The address-parts form of getDirectionsUrl, for non-ScheduledEvent
 * records. Same platform detection and same URL schemes — see above.
 */
export function getDirectionsUrlFromParts(parts: AddressParts): string | null {
    const dest = buildDestinationQueryFromParts(parts);
    if (!dest) return null;
    const encoded = encodeURIComponent(dest);

    if (isIosDevice()) {
        // Apple Maps universal link — opens the Maps app on iOS,
        // falls back to maps.apple.com on the web.
        return `https://maps.apple.com/?daddr=${encoded}`;
    }

    // Google Maps directions URL — works in browser + opens the native
    // Google Maps app on Android via the OS intent system.
    return `https://www.google.com/maps/dir/?api=1&destination=${encoded}`;
}

/**
 * Convenience: open directions for an event in a new tab/window.
 * No-op when the event has no address.
 */
export function openDirections(event: ScheduledEvent): void {
    const url = getDirectionsUrl(event);
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
}
